<template>
  <section class="flex w-full flex-col">
    <UIForm
      id="bumProductSettingsForm"
      ref="bumProductSettingsFormRef"
      :model="bumProductSettings"
      :rules="bumProductSettingsRules"
    >
      <UIFormItem
        id="priceSelect"
        :label="t('common.payment.bumProduct.price')"
        class="min-h-4"
        path="priceId"
      >
        <UISelect
          id="priceSelect"
          v-model:value="bumProductSettings.priceId"
          :options="priceOptions"
          :disabled="isEdit"
        />
      </UIFormItem>
      <UIFormItem
        id="headlineInput"
        :label="t('common.payment.bumProduct.headline')"
        path="headline"
      >
        <UIInput id="headlineInput" v-model="bumProductSettings.headline" />
      </UIFormItem>
      <UIFormItem
        id="otoHeadlineInput"
        :label="t('common.payment.bumProduct.otoHeadline')"
        path="otoHeadline"
      >
        <UIInput id="otoHeadlineInput" v-model="bumProductSettings.otoHeadline" />
      </UIFormItem>
      <UIFormItem id="otoTextInput" :label="t('common.payment.bumProduct.otoText')" path="otoText">
        <UIInput id="otoTextInput" v-model="bumProductSettings.otoText" type="textarea" />
      </UIFormItem>
    </UIForm>
    <div class="flex flex-col">
      <Switch
        id="enableImageSwitch"
        v-model:value="bumProductSettings.enableImage"
        class="text-gray-700"
        :label="t('common.payment.bumProduct.enableImage')"
      />
      <UITextSmRegular class="text-gray-600">{{
        t("common.payment.bumProduct.imageMessage")
      }}</UITextSmRegular>
    </div>

    <div v-if="bumProductSettings.enableImage" class="mt-4 flex flex-col gap-2">
      <UITextSmMedium class="text-gray-700">{{
        t("common.payment.bumProduct.orUploadADifferentImage")
      }}</UITextSmMedium>
      <UIUpload
        class="image-upload"
        :file-list="fileList"
        :multiple="false"
        accept="image/*"
        :max="1"
        @change="handleImageChange"
      >
        <template #extra>
          <UITextSmRegular>{{ t("common.imageUploadMessage") }}</UITextSmRegular>
        </template>
      </UIUpload>
    </div>
  </section>
</template>

<script setup lang="ts">
import Switch from "@/components/common/Elements/Switch.vue";
import { FormServices } from "@/service/FormServices";
import { SurveyServices } from "@/service/SurveyServices";
import { useAppStore } from "@/store/app";
import { BumProduct, Price } from "@/types/product";
import {
  UIForm,
  UIFormItem,
  UIInput,
  UISelect,
  UITextSmMedium,
  UITextSmRegular,
  UIUpload,
} from "@gohighlevel/ghl-ui";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const store = useAppStore();

const props = defineProps<{
  product: BumProduct;
  isEdit: boolean;
}>();
const emits = defineEmits(["update:bumProduct", "validate:bumProductSettings"]);

const fileList = ref([]);
const showLoader = ref(false);
const bumProductSettingsFormRef = ref<InstanceType<typeof UIForm>>();
const priceOptions = computed(() => {
  const prices = props.product.prices;
  return prices.map((price: Price) => ({
    label: price.name,
    value: price._id,
  }));
});
const bumProductSettings = ref({
  priceId: props.product.priceId,
  headline: props.product.headline || "",
  otoHeadline: props.product.otoHeadline || "",
  otoText: props.product.otoText || "",
  enableImage: props.product.enableImage ?? true,
  uploadedImage: props.product.uploadedImage,
  amount: props.product.amount,
  currency: props.product.currency,
});

const bumProductSettingsRules = {
  priceId: [
    { required: true, message: t("common.payment.bumProduct.priceRequired"), trigger: "blur" },
  ],
  headline: [
    { required: true, message: t("common.payment.bumProduct.headlineRequired"), trigger: "blur" },
  ],
  otoHeadline: [
    {
      required: false,
      message: t("common.payment.bumProduct.otoHeadlineRequired"),
      trigger: "blur",
    },
  ],
  otoText: [
    { required: false, message: t("common.payment.bumProduct.otoTextRequired"), trigger: "blur" },
  ],
};
watch(
  () => bumProductSettings.value,
  (newVal, oldVal) => {
    if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
      emits("update:bumProduct", newVal);
      emits("validate:bumProductSettings", bumProductSettingsFormRef.value);
    }
  },
  {
    deep: true,
  }
);

onMounted(() => {
  emits("update:bumProduct", bumProductSettings.value);
  emits("validate:bumProductSettings", bumProductSettingsFormRef.value);
});

async function handleImageChange(e: any) {
  showLoader.value = true;
  const newFileList = e.fileList;

  if (newFileList.length === 0) {
    fileList.value = newFileList;
    showLoader.value = false;
    return;
  }

  const file = newFileList[0].file;

  newFileList[0].status = "uploading";

  try {
    const bodyFormData = new FormData();
    bodyFormData.append("locationId", store.locationId);
    bodyFormData.append("file", file);
    bodyFormData.append("type", "bumProduct");
    let image;
    if (store.formId) {
      bodyFormData.append("formId", store.formId);
      image = await FormServices.imageUpload(bodyFormData);
    } else {
      bodyFormData.append("surveyId", store.surveyId);
      image = await SurveyServices.imageUpload(bodyFormData);
    }

    bumProductSettings.value.uploadedImage = `https://images.leadconnectorhq.com/image/f_webp/q_100/r_1000/u_${image.data.imageUrl}`;

    newFileList[0].status = "finished";
    fileList.value = newFileList;
  } catch (error) {
    newFileList[0].status = "error";
    fileList.value = newFileList;
  }

  showLoader.value = false;
}
</script>

<style>
/* Target all form items within the form */
#bumProductSettingsForm .n-form-item .n-form-item-feedback-wrapper {
  max-height: 8px !important;
}

/* Target the feedback wrapper specifically with higher specificity */
#bumProductSettingsForm :deep(.n-form-item .n-form-item-feedback-wrapper) {
  max-height: 8px !important;
}

/* Alternative approach using attribute selector for better specificity */
#bumProductSettingsForm :deep([class*="n-form-item-feedback-wrapper"]) {
  max-height: 8px !important;
}

/* Global override for all form items in this component */
:deep(.n-form-item-feedback-wrapper) {
  max-height: 8px !important;
}
</style>
